import { media } from '@atlaskit/primitives';
import { Show } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import {
  AppTile,
  PAGINATED_APP_GRID_CONFIG,
  PaginatedAppGrid,
  usePaginatedAppGrid,
} from '@atlassian/mpac-app-tile';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import {
  BackToTopButton,
  BodyNormal,
  Filters,
  FluidContainer,
  H4,
  MatchingLabelSkeleton,
  Meta,
  NoResultView,
  NotFound,
  PartialOutageError,
  SortDropdown,
  WebPageStructuredData,
} from '@atlassian/mpac-primitives';
import { getFilterPropertyValues, useSearchFilters } from '@atlassian/mpac-search';
import { GetMarketplaceCategory } from '@atlassian/mpac-services-client';
import Telemetry from '@atlassian/mpac-telemetry';
import {
  ApolloErrorHandler,
  useLocation,
  usePageRouteSourceProvider,
  useParams,
  useQueryParams,
} from '@atlassian/mpac-ui';
import { useEnvironment, useSource } from '@atlassian/mpac-utils';
import { styled } from '@compiled/react';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import BootstrapConfig from '../../../bootstrapConfig';
import { CategoryPageTask } from '../../../telemetry/tasks';
import { Hero } from '../../common/Hero/Hero';
import { HeroSkeleton } from '../../common/Hero/Hero.styles';
import {
  defaultFilterDefinitions,
  transformedCategoryFilterDefinitions,
} from './filters/categoryFiltersConfig';
import { useCategoryPageAnalytics, useCategoryTelemetry } from './hooks';

import type { TileData } from '@atlassian/mpac-app-tile';
import type {
  GetMarketplaceCategoryQuery,
  GetMarketplaceCategoryQueryVariables,
} from '@atlassian/mpac-services-client';

const Container = styled.div({
  paddingBottom: token('space.800'),
});

const HeroWrapper = styled.div({
  marginBottom: token('space.500'),
});

export const ErrorContainer = styled.div({
  position: 'absolute',
  width: '100%',
  height: '100%',
  padding: `${token('space.400')}`,
});

const FilterWrapper = styled.div({
  marginTop: token('space.200'),
  marginBottom: token('space.400'),

  [media.below.md]: {
    marginBottom: token('space.300'),
  },

  [media.below.sm]: {
    marginTop: token('space.150'),
  },
});

const SortAndCountWrapper = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: token('space.200'),
});

const H3 = ({ children, ...props }) => (
  <BodyNormal as="h3" {...props}>
    {children}
  </BodyNormal>
);

const BodyStyled = styled(H3)({
  fontWeight: 400,
});

// eslint-disable-next-line max-lines-per-function, complexity
const CategoryPage = () => {
  const { slug } = useParams();
  const { useCases } = useQueryParams();
  const { pathname, search } = useLocation();
  const [showPageError, setShowPageError] = useState(false);
  const previousSlug = useRef(slug);
  const { enableFlexibleBillingFilter } = useStatsig();

  const filterDefinition = enableFlexibleBillingFilter()
    ? transformedCategoryFilterDefinitions
    : defaultFilterDefinitions;

  const {
    // states
    allFilterDefinitions,
    filterOptions,
    selectedOptions,
    filterStatus,
    sortOptions,
    commitSortKey,
    commitOptions,
    selectedMobileSortOption,
    // booleans
    showAllFilters,

    // set states
    setShowAllFilters,

    // functionality
    onClearFilter,
    onClearAllFilters,
    onSelectedOptionsChange,
    onApplyFilters,
    onSelectedSortOptionChange,
    onCloseMobileDialog,
    onCommitSortOptionSelect,
    handleClearFilters,
  } = useSearchFilters(filterDefinition);

  useEffect(() => {
    CategoryPageTask.start();
  }, []);

  useLayoutEffect(() => {
    Telemetry.timeToFirstMeaningfulPaint(performance.now());
  }, []);

  const [category, setCategory] = useState<
    NonNullable<GetMarketplaceCategoryQuery['marketplaceCategory']>['category'] | undefined
  >();

  useEffect(() => {
    if (filterStatus.useCases === 'error' && useCases) {
      setShowPageError(true);
    }
  }, [filterStatus, useCases]);

  const source = useSource();
  const environment = useEnvironment();
  const { pageRouteSource, setPageRouteSource } = usePageRouteSourceProvider();
  const [capturedRouteSource, setCapturedRouteSource] = useState<string | null>(null);

  const {
    appList,
    totalCount,
    isLoading,
    isLoadingMore,
    showLoadMoreButton,
    fetchMoreApps,
    initialFetchError,
    fetchMoreError,
    rawData,
  } = usePaginatedAppGrid<GetMarketplaceCategoryQuery, GetMarketplaceCategoryQueryVariables>({
    query: GetMarketplaceCategory,
    queryOptions: {
      variables: {
        slug: slug ?? '',
        filters: commitOptions,
        sort: commitSortKey,
        after: 0,
        first: PAGINATED_APP_GRID_CONFIG.initialFetchCount,
        skipCategory: !!category,
      },
    },
    parseResponse: (data) => {
      return {
        appList: data?.marketplaceCategory?.apps?.edges?.map((edge) => edge.node as TileData) || [],
        hasNextPage: Boolean(data?.marketplaceCategory?.apps?.pageInfo.hasNextPage),
        totalCount: data?.marketplaceCategory?.apps?.pageInfo.totalCount || 0,
      };
    },
  });

  // Added to avoid render issues on Server due to category being undefined on first render
  const categoryData = category || rawData?.marketplaceCategory?.category;

  useEffect(() => {
    setCategory(undefined);
  }, [slug]);

  useEffect(() => {
    if (rawData?.marketplaceCategory?.category) {
      setCategory(rawData?.marketplaceCategory?.category);
    }
  }, [rawData?.marketplaceCategory?.category]);

  useEffect(() => {
    if (pageRouteSource) {
      setCapturedRouteSource(pageRouteSource);
      setPageRouteSource(null);
    }
  }, [pageRouteSource, setPageRouteSource]);

  useLayoutEffect(() => {
    // Total count can be updated when filters are applied as well,
    // we intend to track the overall page load & transitions via perf event
    if (previousSlug.current !== slug && totalCount > 0) {
      previousSlug.current = slug;
      Telemetry.timeToFirstMeaningfulPaint(performance.now());
    }
  }, [slug, totalCount]);

  useCategoryTelemetry({
    slug,
    initialFetchError,
    category,
    isLoadingMore,
    fetchMoreError,
    appList: rawData?.marketplaceCategory?.apps,
    filters: commitOptions,
    sortKey: commitSortKey,
  });

  const appliedFilters = useMemo(
    () => getFilterPropertyValues(commitOptions, allFilterDefinitions),
    [commitOptions, allFilterDefinitions]
  );

  const {
    fireOnTileClickEvent,
    fireOnLoadMoreClickEvent,
    fireOnClearFiltersClickEvent,
    fireOnSortOptionSelectEvent,
  } = useCategoryPageAnalytics({
    slug,
    initialFetchError,
    category,
    fetchMoreError,
    appsCount: appList?.length || 0,
    sortKey: commitSortKey || sortOptions?.[0].value,
    selectedOptions,
    isLoading,
    isLoadingMore,
    pageRouteSource: capturedRouteSource,
  });

  const handleOnTileClick = (app: TileData, index: number) => {
    const attributes = {
      categoryType: category?.slug,
      hitRank: index + 1,
    };
    fireOnTileClickEvent(attributes, app);
    setPageRouteSource('categoryPage');
  };

  const handleOnLoadMoreClick = async () => {
    const attributes = {
      categoryType: category?.slug,
      pageNumber: Math.ceil(appList.length / PAGINATED_APP_GRID_CONFIG.fetchMoreCount),
    };
    fireOnLoadMoreClickEvent(attributes);

    fetchMoreApps({
      first: PAGINATED_APP_GRID_CONFIG.fetchMoreCount,
      variables: { skipCategory: true },
    });
  };

  const handleOnClearFiltersClick = () => {
    fireOnClearFiltersClickEvent();

    handleClearFilters();
  };

  const handleOnSortOptionSelect = (sortOption) => {
    const attributes = {
      categoryType: category?.slug,
      filterType: 'sort',
      filterValue: sortOption.value,
    };
    fireOnSortOptionSelectEvent(attributes);

    onCommitSortOptionSelect(sortOption);
  };

  const showNoResultView = !isLoading && totalCount === 0 && !initialFetchError;

  // If slug exist but  there is initialfetcherror and networkerror is 401
  if (slug && initialFetchError) {
    //When slug exists but initialfetcherror is not found that is 404
    if (initialFetchError?.message === 'NOT_FOUND') {
      return <NotFound />;
    }

    return (
      <ApolloErrorHandler
        error={initialFetchError}
        context={{ page: 'CategoryPage', operationName: 'GetMarketplaceCategory' }}
        renderError={() => {
          CategoryPageTask.failed(initialFetchError);
          return (
            <ErrorContainer data-testid="error">
              <PartialOutageError level="global" />
            </ErrorContainer>
          );
        }}
        nonServerErrorCallback={() => CategoryPageTask.abort()}
      />
    );
  }

  //If slug does not exist  or there is initialfetcherror but category is not found or showpageerror is true
  if (!slug || (initialFetchError && !category) || showPageError) {
    return (
      <ErrorContainer>
        <PartialOutageError level="global" />
      </ErrorContainer>
    );
  }

  const { mpacBaseUrl } = BootstrapConfig.get();
  const canonicalUrl = categoryData ? `${mpacBaseUrl}${pathname}${search}` : undefined;

  const metaTitle = categoryData ? `Apps for ${categoryData.name}` : 'Apps for Atlassian Products';

  return (
    <Container>
      {categoryData && (
        <>
          <Meta
            title={metaTitle}
            description={categoryData.heroSection.description}
            canonicalUrl={canonicalUrl}
          />
          <WebPageStructuredData
            title={metaTitle}
            description={categoryData.heroSection.description}
            canonicalUrl={canonicalUrl}
            image={categoryData.heroSection.image.url}
            additionalProperty={appliedFilters.length > 0 ? appliedFilters : undefined}
          />
        </>
      )}
      <HeroWrapper>
        {categoryData ? (
          <Hero
            backgroundColor={token('color.background.accent.gray.subtlest')}
            secondaryBreadcrumbTitle={categoryData.name}
            title={categoryData.heroSection.title}
            description={categoryData.heroSection.description}
            imageUrl={categoryData.heroSection.image.url}
            imageAltText={categoryData.heroSection.image.altText}
          />
        ) : (
          <HeroSkeleton />
        )}
      </HeroWrapper>
      <FluidContainer>
        <H4 as="h2">Browse apps</H4>
        <FilterWrapper>
          <Filters
            filterOptions={filterOptions}
            filterDefinitions={allFilterDefinitions}
            filterStatus={filterStatus}
            showAllFilters={showAllFilters}
            onShowAllFilters={() => setShowAllFilters(!showAllFilters)}
            selectedOptions={selectedOptions}
            onSelectedOptionsChange={onSelectedOptionsChange}
            sortOptions={sortOptions}
            selectedSortOption={selectedMobileSortOption}
            onSelectedSortOptionChange={onSelectedSortOptionChange}
            onClearFilter={onClearFilter}
            onClearAllFilters={onClearAllFilters}
            onApplyFilters={onApplyFilters}
            onCloseMobileDialog={onCloseMobileDialog}
          />
        </FilterWrapper>
        <SortAndCountWrapper>
          {isLoading ? (
            <MatchingLabelSkeleton />
          ) : (
            <BodyStyled>Showing {totalCount} apps</BodyStyled>
          )}
          <Show above="sm" as="span">
            <SortDropdown
              onSortOptionSelect={handleOnSortOptionSelect}
              selectedSortKey={commitSortKey}
            />
          </Show>
        </SortAndCountWrapper>
        {showNoResultView ? (
          <NoResultView
            showClearFilterButton
            title="We couldn’t find apps that meet your criteria!"
            description="Try removing or modifying a filter"
            onClearFiltersClick={handleOnClearFiltersClick}
          />
        ) : (
          <PaginatedAppGrid
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            onLoadMoreClick={handleOnLoadMoreClick}
            showLoadMoreButton={showLoadMoreButton}
            renderGrid={() =>
              appList.map((app, index) => (
                <AppTile
                  key={index}
                  tileData={app as TileData}
                  environment={environment}
                  source={source}
                  onTileClick={() => handleOnTileClick(app, index)}
                />
              ))
            }
            fetchMoreError={fetchMoreError}
            initialFetchError={initialFetchError}
          />
        )}
      </FluidContainer>
      <BackToTopButton />
    </Container>
  );
};

export default CategoryPage;
